import { useEffect } from "react";
import {
  StackLayout,
  HeadingText,
  ParagraphText,
  TextAction,
  useMediaQuery,
  queryHelpers,
  ImageMessage,
} from "@leagueplatform/genesis-core";
import type { GDSStyleObject } from "@leagueplatform/genesis-core";
import {
  EVENT_NAME,
  PRODUCT_AREA,
  SCREEN_NAMES,
  trackAnalyticsEvent,
} from "@leagueplatform/analytics";
import { useIntl } from "@leagueplatform/locales";
import { BaseModalContainer } from "components/modals/common/base-modal.component";
import { useDeleteAccount } from "hooks/mutations/use-delete-account.hook";
import deleteAccountWarning from "assets/delete-account-warning.svg";
import { ErrorState } from "@leagueplatform/web-common-components";
import { StandaloneAuth } from "@leagueplatform/auth-standalone";
import {
  ANALYTICS_DETAILS,
  QUERY_PARAM_KEYS,
  QUERY_PARAM_MODAL_VALUE,
} from "common/constants";
import { EXTERNAL_LINKS } from "common/links";
import { LoadingSpinner } from "components/loading-spinner/loading-spinner.component";
import { useGetUserProfile } from "hooks/queries/use-get-user-profile.hook";
import { ModalActionButton } from "./common/modal-action-button.component";

const inlineLinkStyle: GDSStyleObject = {
  typography: "$bodyOne",
  display: "inline",
  textDecoration: "underline",
};

export const DeleteAccountModal = ({ onClose }: { onClose: () => void }) => {
  const { formatMessage } = useIntl();
  useEffect(() => {
    trackAnalyticsEvent(EVENT_NAME.SCREEN_LOADED, {
      product_area: PRODUCT_AREA.SETTINGS,
      screen_name: SCREEN_NAMES.DELETE_ACCOUNT,
    });
  }, []);

  const isMobile = useMediaQuery(queryHelpers.only("mobile"));
  const {
    mutateAsync,
    isLoading: isAccountDeleteLoading,
    isError,
    reset,
  } = useDeleteAccount();
  const { data, isSuccess, isLoading, error } = useGetUserProfile();

  if (isLoading) {
    return (
      <BaseModalContainer>
        <LoadingSpinner />
      </BaseModalContainer>
    );
  }

  if (!isSuccess || isError) {
    return (
      <BaseModalContainer>
        <ErrorState error={error} onButtonClick={reset} />
      </BaseModalContainer>
    );
  }

  const { userId } = data;
  const handleDeleteAccount = async () => {
    await mutateAsync(userId, {
      onSuccess: () => {
        const queryParam = new URLSearchParams();
        queryParam.set(
          QUERY_PARAM_KEYS.MODAL,
          QUERY_PARAM_MODAL_VALUE.DELETE_ACCOUNT_DISCLAIMER
        );
        onClose();
        StandaloneAuth.client.logout({
          federated: true,
          returnTo: `${window.location.origin}?${queryParam.toString()}`,
        });
      },
    });
  };

  return (
    <BaseModalContainer
      title={formatMessage({ id: "STR_DELETE_ACCOUNT_TITLE" })}
      footer={
        <StackLayout
          horizontalAlignment={{ "@initial": "end", "@mobile": "center" }}
          orientation={{
            "@initial": "horizontal",
            "@mobile": "vertical",
          }}
          spacing="$one"
          css={{
            flexGrow: 0,
            "@mobile": {
              flexFlow: "column-reverse",
            },
          }}
        >
          <ModalActionButton
            actionType="cancel"
            buttonText={formatMessage({ id: "STR_NEVER_MIND" })}
            isMobile={isMobile}
            css={{ color: "$onSurfaceTextSubdued" }}
            trackAnalytics={{
              product_area: PRODUCT_AREA.SETTINGS,
              screen_name: SCREEN_NAMES.DELETE_ACCOUNT,
              detail: ANALYTICS_DETAILS.CANCEL,
            }}
          />
          <ModalActionButton
            actionType="submit"
            onClick={handleDeleteAccount}
            buttonText={formatMessage({ id: "STR_YES_DELETE_ACCOUNT" })}
            isMobile={isMobile}
            destructive
            loading={isAccountDeleteLoading}
            disabled={isAccountDeleteLoading}
            trackAnalytics={{
              product_area: PRODUCT_AREA.SETTINGS,
              screen_name: SCREEN_NAMES.DELETE_ACCOUNT,
              detail: ANALYTICS_DETAILS.CONFIRM_DELETION,
            }}
          />
        </StackLayout>
      }
    >
      <ImageMessage
        image={deleteAccountWarning}
        css={{
          flexGrow: 1,
          padding: "$two $none",
          textAlign: "left",
          "@mobile": { padding: "$half" },
        }}
      >
        <HeadingText
          level="3"
          size="lg"
          css={{
            paddingBlock: "$one",
            textAlign: "left",
          }}
        >
          {formatMessage({ id: "STR_ARE_YOU_SURE" })}
        </HeadingText>
        <ParagraphText>
          {formatMessage(
            { id: "STR_DELETE_ACCOUNT_BODY_1" },
            {
              myHealthQuestLink: (
                <TextAction
                  target="_blank"
                  rel="noopener noreferrer"
                  as="a"
                  href={EXTERNAL_LINKS.MY_HEALTH_QUEST}
                  css={inlineLinkStyle}
                >
                  {formatMessage({ id: "STR_MYHEALTHQUEST_LINK_TEXT" })}
                </TextAction>
              ),
            }
          )}
        </ParagraphText>
        <ParagraphText>
          {formatMessage({ id: "STR_DELETE_ACCOUNT_BODY_2" })}
        </ParagraphText>
      </ImageMessage>
    </BaseModalContainer>
  );
};
